import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Check, Copy } from "lucide-react";
import type { CodeSample } from "../shared/types";
import { COPY } from "../copy";

/**
 * Code on the docs: one block (`CodeBlock`) or one request in several languages (`CodeGroup`).
 * The HTML is Shiki's, made at build time with a light and a dark theme; the dark colours apply
 * under `[data-theme="dark"]` through Shiki's `--shiki-dark` variables.
 *
 * The reader's language is one choice for the whole site (design §0.2): picking a tab sets it for
 * every group on the page and is remembered for the next visit.
 */

const LANG_KEY = "refabric-docs-lang";
const LANG_EVENT = "refabric-docs-lang";

const readLang = (): string | null => {
  try {
    return window.localStorage.getItem(LANG_KEY);
  } catch {
    return null;
  }
};

export const useCodeLang = (): [string | null, (lang: string) => void] => {
  const [lang, setLangState] = useState<string | null>(readLang);

  useEffect(() => {
    const onChange = (event: Event) => setLangState((event as CustomEvent<string>).detail);
    window.addEventListener(LANG_EVENT, onChange);

    return () => window.removeEventListener(LANG_EVENT, onChange);
  }, []);

  const setLang = useCallback((next: string) => {
    try {
      window.localStorage.setItem(LANG_KEY, next);
    } catch {
      // Private mode: the choice lasts for this page only.
    }
    window.dispatchEvent(new CustomEvent(LANG_EVENT, { detail: next }));
  }, []);

  return [lang, setLang];
};

const SHIKI_CLASSES = "overflow-x-auto text-bodySmall [&_pre]:!bg-transparent [&_pre]:p-4 [&_code]:[font-family:ui-monospace,SFMono-Regular,Menlo,monospace] dark:[&_span]:![color:var(--shiki-dark)]";

export const CopyButton = ({ text, className }: { text: string; className?: string }) => {
  const [done, setDone] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setDone(true);
      window.setTimeout(() => setDone(false), 1500);
    });
  };

  return (
    <button type="button" onClick={onCopy} className={clsx("rounded p-1 text-muted-foreground hover:text-foreground", className)} aria-label={done ? COPY.copied : COPY.copy}>
      {done ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

const CodeHtml = ({ sample }: { sample: CodeSample }) => <div className={SHIKI_CLASSES} dangerouslySetInnerHTML={{ __html: sample.html }} />;

/** A single highlighted block with its copy button; `title` is a file path or a label. */
export const CodeBlock = ({ sample, title }: { sample: CodeSample; title?: string }) => (
  <div className="my-4 overflow-hidden rounded-lg border border-border bg-card">
    {title ? (
      <div className="flex items-center justify-between border-b border-border px-4 py-2 text-bodySmall text-muted-foreground">
        <span>{title}</span>
        <CopyButton text={sample.code} />
      </div>
    ) : null}
    <div className="relative">
      {title ? null : <CopyButton text={sample.code} className="absolute right-2 top-2" />}
      <CodeHtml sample={sample} />
    </div>
  </div>
);

/**
 * Tabs of code. With `syncLang` (the default) the tabs are languages of one request and follow the
 * reader's language; without it they are independent views (an answer per status code).
 */
export const CodeGroup = ({ samples, className, syncLang = true }: { samples: CodeSample[]; className?: string; syncLang?: boolean }) => {
  const [lang, setLang] = useCodeLang();
  const [picked, setPicked] = useState(0);
  if (samples.length === 0) return null;
  const active = syncLang ? samples.find((s) => s.lang === lang) ?? samples[0] : samples[Math.min(picked, samples.length - 1)];

  return (
    <div className={clsx("my-4 overflow-hidden rounded-lg border border-border bg-card", className)}>
      <div className="flex items-center justify-between border-b border-border px-2">
        <div className="flex min-w-0 gap-1 overflow-x-auto" role="tablist">
          {samples.map((sample) => (
            <button
              key={`${sample.lang}-${sample.label}`}
              type="button"
              role="tab"
              aria-selected={sample === active}
              onClick={() => (syncLang ? setLang(sample.lang) : setPicked(samples.indexOf(sample)))}
              className={clsx("border-b-2 px-3 py-2 text-bodySmall", sample === active ? "border-purpleGreen text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}
            >
              {sample.label}
            </button>
          ))}
        </div>
        <CopyButton text={active.code} />
      </div>
      <CodeHtml sample={active} />
    </div>
  );
};
